import { call, put } from 'redux-saga/effects';
import moment from 'moment';

import { AppointmentsTypes as Types } from '../ducks/appointments';
import { sagaErrorHandle } from './sagasError';
import * as service from '../../services/appointments';
import { convertArrToObj } from '../../utils';

export function* getAppointmentInfos() {
  try {
    const { data: infos } = yield call(service.fetchInfos);
    yield put({ type: Types.SET_INFOS, infos });
  } catch (error) {
    yield sagaErrorHandle({
      sagaName: 'getAppointmentInfos',
      errorData: error,
    });
  }
}

export function* getAppointments() {
  try {
    const { data: appointments } = yield call(service.listAppointments);
    const appointmentsObj = convertArrToObj(appointments, 'id');
    yield put({ type: Types.SET_APPOINTMENTS, appointmentsObj });
  } catch (error) {
    yield sagaErrorHandle({
      sagaName: 'getAppointments',
      errorData: error,
    });
  }
}

export function* getProfessionals() {
  try {
    const { data: professionals } = yield call(service.listProfessionals);
    const manipulator = item => ({ ...item, allLoaded: true });
    const professionalsObj = convertArrToObj(professionals, 'id', manipulator);
    yield put({ type: Types.SET_PROFESSIONALS, professionalsObj });
  } catch (error) {
    yield sagaErrorHandle({ sagaName: 'getProfessionals', errorData: error });
  }
}

export function* getProfessional(action) {
  const { id: professionalId } = action;
  try {
    const { data: professionalInfos } = yield call(
      service.fetchProfessional,
      professionalId,
    );
    yield put({
      type: Types.SET_PROFESSIONAL,
      professionalId,
      professionalInfos,
    });
  } catch (error) {
    yield sagaErrorHandle({
      sagaName: 'getProfessional',
      errorData: error,
      additionalInfos: { professionalId },
    });
  }
}

export function* cancelAppointment(action) {
  const { id: appointmentId } = action;
  try {
    const {
      data: { canceled: removed, absent: userInfoAbsence },
    } = yield call(service.cancelAppointment, appointmentId);
    yield put({
      type: Types.UPDATE_APPOINTMENT,
      appointmentId,
      appointmentInfos: {
        removed,
        userInfoAbsence,
        error: {},
        success: {
          action: 'cancelAppointment',
          msg: removed
            ? 'Agendamento cancelado com sucesso!'
            : 'Ausência informada com sucesso!',
        },
      },
    });
  } catch (error) {
    yield sagaErrorHandle({
      sagaName: 'cancelAppointment',
      errorData: error,
      additionalInfos: { appointmentId },
    });
    yield put({
      type: Types.UPDATE_APPOINTMENT,
      appointmentId,
      appointmentInfos: {
        error: {
          action: 'cancelAppointment',
          msg: 'Ocorreu um erro ao realizar a ação. Tente novamente.',
        },
        success: {},
      },
    });
  }
}

export function* storeRating(action) {
  const { appointmentId, rating, comments } = action;
  try {
    yield call(service.sendRating, {
      appointmentId,
      rating,
      comments,
    });
    yield put({
      type: Types.UPDATE_APPOINTMENT,
      appointmentId,
      appointmentInfos: {
        rating,
        comments,
        error: {},
        success: {
          action: 'storeRating',
          msg: 'Avaliação enviada com sucesso!',
        },
      },
    });
  } catch (error) {
    yield sagaErrorHandle({
      sagaName: 'storeRating',
      errorData: error,
      additionalInfos: action,
    });
    yield put({
      type: Types.UPDATE_APPOINTMENT,
      appointmentId,
      appointmentInfos: {
        error: {
          action: 'storeRating',
          msg: 'Não foi possível enviar sua avaliação.',
        },
        success: {},
      },
    });
  }
}

export function* getScheduleTimeTable() {
  try {
    const { data: timetable } = yield call(service.listScheduleTimeTable);
    const manipulator = item => {
      const { timeId: id, ...rest } = item;
      return { id, ...rest };
    };
    const timetableObj = convertArrToObj(timetable, 'timeId', manipulator);
    yield put({
      type: Types.SET_SCHEDULE_TIME_TABLE,
      timetable: timetableObj,
    });
  } catch (error) {
    yield sagaErrorHandle({
      sagaName: 'getScheduleTimeTable',
      errorData: error,
    });
  }
}

export function* getScheduleColumns(action) {
  const { startDate, endDate, professional } = action;
  const length = moment(endDate).diff(moment(startDate), 'd') + 1;
  const columnsRequested = Array.from({ length }, (v, index) => ({
    date: moment(startDate)
      .utc()
      .startOf('day')
      .add(index, 'd')
      .toISOString(true),
    slots: [],
    loaded: true,
  }));
  const columnsRequestedObj = convertArrToObj(columnsRequested, 'date');
  let mergedColumns = columnsRequestedObj;
  try {
    let filters = {};
    if (professional) {
      filters = { professional };
    }
    const { data: columns } = yield call(service.listScheduleColumns, {
      startDate,
      endDate,
      filters,
    });
    const manipulator = item => ({
      ...item,
      date: moment(item.date).utc().startOf('day').toISOString(true),
      loaded: true,
    });
    const columnsObj = columns.length
      ? convertArrToObj(columns, 'date', manipulator, true)
      : {};
    mergedColumns = {
      ...columnsRequestedObj,
      ...columnsObj,
    };
  } catch (error) {
    yield sagaErrorHandle({
      sagaName: 'getScheduleColumns',
      errorData: error,
      additionalInfos: action,
    });
  } finally {
    yield put({
      type: Types.UPDATE_SCHEDULE_COLUMNS,
      columnsObj: mergedColumns,
      professional,
    });
  }
}

export function* storeAppointment(action) {
  const {
    timeTableId,
    columnDate,
    professionalId,
    meetingId,
    contact,
    mainDoubt,
  } = action;
  yield put({
    type: Types.UPDATE_NEW_APPOINTMENT,
    sending: true,
  });
  try {
    const { data: newAppointment } = yield call(service.sendAppointment, {
      timeTableId,
      columnDate: moment(columnDate).utc().startOf('d').format('MM/DD/YYYY'),
      professionalId,
      meetingId,
      contact,
      mainDoubt,
    });
    yield put({ type: Types.RESET_APPOINTMENTS });
    yield put({
      type: Types.UPDATE_NEW_APPOINTMENT,
      sending: false,
      id: newAppointment.id,
      error: '',
    });
  } catch (error) {
    yield sagaErrorHandle({
      sagaName: 'storeAppointment',
      errorData: error,
      additionalInfos: action,
    });
    const getErrorMessage = () => {
      const msgError = error.response.data.error;
      switch (msgError) {
        case 'Selected time does not exists.':
          return {
            type: 'TIME_NOT_EXISTS',
            message: 'O horário selecionado não está mais disponível.',
          };
        case 'User without permission to schedule.':
          return {
            type: 'WITHOUT_PERMISSION',
            message: 'Você ainda não tem permissão para realizar agendamentos.',
          };
        case 'Schedule limit exceeded.':
          return {
            type: 'LIMIT_EXCEEDED',
            message: 'Você já atingiu seu limite de agendamentos',
          };
        case 'The selected time is no longer available.':
          return {
            type: 'NOT_AVAILABLE_TIME',
            message: 'O horário selecionado não está mais disponível.',
          };
        case 'Scheduling date is before now.':
          return {
            type: 'TIME_IS_BEFORE_NOW',
            message:
              'Não é mais possível realizar um agendamento para este horário.',
          };
        case 'Internal Error':
        default:
          return {
            type: 'GENERIC_ERROR',
            message: 'Ocorreu um erro no envio do seu agendamento.',
          };
      }
    };
    const errorMessage = getErrorMessage();
    yield put({
      type: Types.UPDATE_NEW_APPOINTMENT,
      sending: false,
      id: null,
      error: errorMessage,
    });
  }
}
